import type { TaskStage, TaskSummary } from '@obras/shared';
import { PERSONAL_BOARD_ID } from '@obras/shared';
import { Pencil, Plus, SquareKanban, User } from 'lucide-react';
import { useState } from 'react';

import { AppShell, PageHeader } from '../components/AppShell.js';
import { Button } from '../components/ui/Button.js';
import { ErrorState, RefreshingBar, Skeleton } from '../components/ui/Feedback.js';
import { Modal } from '../components/ui/Modal.js';
import { BoardFormModal } from '../features/tarefas/BoardFormModal.js';
import { KanbanBoard } from '../features/tarefas/KanbanBoard.js';
import { TaskFormModal } from '../features/tarefas/TaskFormModal.js';
import { TaskViewModal } from '../features/tarefas/TaskViewModal.js';
import { FLAG_TEXT } from '../features/tarefas/styles.js';
import {
  useRemoveBoard,
  useBoard,
  useBoards,
  useCreateColumn,
  useDeleteColumn,
  useUpdateTask,
} from '../features/tarefas/api.js';
import { cn } from '../lib/cn.js';

const ICON = { strokeWidth: 1.5 } as const;

function BoardSkeleton() {
  return (
    <div className="flex gap-3">
      {Array.from({ length: 3 }, (_, index) => (
        <div key={index} className="flex w-72 flex-col gap-2 rounded-lg bg-muted p-2">
          <Skeleton className="mx-1.5 mt-1.5 h-3 w-24" />
          <Skeleton className="h-16" />
          <Skeleton className="h-16" />
        </div>
      ))}
    </div>
  );
}

export function TarefasPage() {
  const [boardId, setBoardId] = useState<string>(PERSONAL_BOARD_ID);
  const [boardFormOpen, setBoardFormOpen] = useState(false);
  const [viewing, setViewing] = useState<TaskSummary | null>(null);
  const [editing, setEditing] = useState<TaskSummary | null>(null);
  const [boardEditOpen, setBoardEditOpen] = useState(false);
  const [creatingAt, setCreatingAt] = useState<string | null>(null);
  const [removeOpen, setRemoveOpen] = useState(false);

  const boards = useBoards();
  const current = useBoard(boardId);
  const move = useUpdateTask(boardId);
  const addColumn = useCreateColumn(boardId);
  const removeColumn = useDeleteColumn(boardId);
  const remove = useRemoveBoard();

  const board = current.data?.board;

  function handleMove(taskId: string, columnId: string, position: number): void {
    if (!board) return;

    /*
     * No Pessoal a "coluna" é a etapa: o identificador que o quadro devolve já
     * é o valor do enum, então não há tradução a fazer aqui.
     */
    void move.mutateAsync({
      id: taskId,
      position,
      ...(board.isPersonal ? { stage: columnId as TaskStage } : { columnId }),
    });
  }

  return (
    <AppShell>
      <PageHeader
        title="Tarefas"
        description="O quadro Pessoal reúne as tarefas que você criou e aquelas em que foi designado."
        action={
          board ? (
            <Button
              variant="primary"
              icon={<Plus {...ICON} size={16} />}
              onClick={() => setCreatingAt(board.columns[0]?.id ?? null)}
            >
              Nova tarefa
            </Button>
          ) : null
        }
      />

      {/* Trilho de quadros: o Pessoal primeiro, sempre. */}
      <div className="mb-4 flex flex-wrap items-center gap-2">
        {(boards.data?.items ?? []).map((item) => (
          <button
            key={item.id}
            type="button"
            onClick={() => setBoardId(item.id)}
            aria-current={item.id === boardId ? 'true' : undefined}
            className={cn(
              'flex h-9 items-center gap-2 rounded-sm px-3 text-[13px] transition-colors duration-100',
              item.id === boardId
                ? 'bg-foreground font-medium text-white'
                : 'bg-muted text-foreground hover:bg-accent',
            )}
          >
            {item.isPersonal ? (
              <User {...ICON} size={15} aria-hidden />
            ) : (
              <SquareKanban
                {...ICON}
                size={15}
                className={cn(
                  'shrink-0',
                  item.color && item.id !== boardId ? FLAG_TEXT[item.color] : undefined,
                )}
                aria-hidden
              />
            )}
            {item.name}
            <span
              className={cn(
                'tabular text-[11px]',
                item.id === boardId ? 'text-white/55' : 'text-muted-foreground',
              )}
            >
              {item.taskCount}
            </span>
          </button>
        ))}

        <Button
          variant="ghost"
          size="sm"
          icon={<Plus {...ICON} size={15} />}
          onClick={() => setBoardFormOpen(true)}
        >
          Novo quadro
        </Button>

        {board && !board.isPersonal && board.canManage ? (
          <div className="ml-auto flex items-center gap-2">
            <Button
              size="sm"
              icon={<Pencil {...ICON} size={15} />}
              onClick={() => setBoardEditOpen(true)}
            >
              Editar quadro
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="text-danger"
              onClick={() => setRemoveOpen(true)}
            >
              {board.isEmpty ? 'Excluir' : 'Arquivar'}
            </Button>
          </div>
        ) : null}
      </div>

      <div className="mb-4">
        <RefreshingBar active={current.isFetching && !current.isPending} />
      </div>

      {current.isError ? (
        <ErrorState
          message={current.error instanceof Error ? current.error.message : 'Erro desconhecido.'}
          onRetry={() => void current.refetch()}
        />
      ) : current.isPending || !board ? (
        <BoardSkeleton />
      ) : (
        <>
          {/*
            Quadro vazio continua mostrando as colunas: escondê-las atrás de um
            aviso tirava justamente a estrutura que diz onde a primeira tarefa
            vai cair, e o "+" de cada coluna é o caminho mais curto para criá-la.
          */}
          {current.data.tasks.length === 0 ? (
            <p className="mb-3 text-[13px] text-muted-foreground">
              {board.isPersonal
                ? 'Nada por aqui ainda. Crie uma tarefa, ou espere alguém incluir você em uma.'
                : 'Quadro vazio. Use o + de uma coluna para começar.'}
            </p>
          ) : null}

          <KanbanBoard
            board={board}
            tasks={current.data.tasks}
            onOpenTask={setViewing}
            onEditTask={setEditing}
            onAdd={setCreatingAt}
            onMove={handleMove}
            onRemoveColumn={(columnId) => void removeColumn.mutateAsync(columnId)}
            onAddColumn={(name) => void addColumn.mutateAsync({ name })}
          />
        </>
      )}

      {board && creatingAt !== null ? (
        <TaskFormModal
          open
          onClose={() => setCreatingAt(null)}
          board={board}
          initialPlacement={creatingAt}
        />
      ) : null}

      {board && viewing ? (
        <TaskViewModal
          open
          onClose={() => setViewing(null)}
          board={board}
          task={current.data?.tasks.find((task) => task.id === viewing.id) ?? viewing}
        />
      ) : null}

      {board && editing ? (
        <TaskFormModal
          open
          onClose={() => setEditing(null)}
          board={board}
          task={current.data?.tasks.find((task) => task.id === editing.id) ?? editing}
        />
      ) : null}

      {board && !board.isPersonal && boardEditOpen ? (
        <BoardFormModal open onClose={() => setBoardEditOpen(false)} board={board} />
      ) : null}

      <BoardFormModal
        open={boardFormOpen}
        onClose={() => setBoardFormOpen(false)}
        onCreated={setBoardId}
      />

      {/*
        Quadro sem tarefa nenhuma é apagado de vez — não há histórico a
        guardar. Com tarefa, arquiva. O texto diz qual dos dois vai acontecer,
        porque as consequências são bem diferentes.
      */}
      <Modal
        open={removeOpen}
        onClose={() => setRemoveOpen(false)}
        title={board?.isEmpty ? 'Excluir quadro' : 'Arquivar quadro'}
        description="O quadro deixa de aparecer para você e para os demais participantes."
        footer={
          <>
            <Button
              variant="ghost"
              onClick={() => setRemoveOpen(false)}
              disabled={remove.isPending}
            >
              Cancelar
            </Button>
            <Button
              variant="danger"
              loading={remove.isPending}
              onClick={() => {
                if (!board) return;
                void remove.mutateAsync(board.id).then(() => {
                  setBoardId(PERSONAL_BOARD_ID);
                  setRemoveOpen(false);
                });
              }}
            >
              {board?.isEmpty ? 'Excluir' : 'Arquivar'}
            </Button>
          </>
        }
      >
        <p className="text-muted-foreground">
          {board?.isEmpty ? (
            <>
              <strong className="font-semibold">{board.name}</strong> nunca teve tarefa, então é
              apagado de vez, junto com as colunas. Não há como voltar atrás.
            </>
          ) : (
            <>
              Confirma arquivar <strong className="font-semibold">{board?.name}</strong>? As tarefas
              continuam gravadas e um administrador pode reverter no banco.
            </>
          )}
        </p>
      </Modal>
    </AppShell>
  );
}
